import { DateTime } from 'luxon';

import { toISODateString } from 'common/utils';
import type { FilterData } from 'portlet/models';
import type {
    DatabricksFilterDefault,
    DatabricksQueryColumn,
} from 'portlet/models/query/systems/databricks/types';
import type { FilterValueType } from 'portlet/models/filterValue';
import { FilterValue } from 'portlet/models/filterValue';

export const dataBricksDateColumnType = ['date', 'timestamp', 'timestamp_ntz'];

type BaseFormatedColumnValue = {
    filterType: FilterValueType;
    isValueArray: boolean;
    value: string;
};

export type FormattedColumnValue =
    | BaseFormatedColumnValue
    | {
          filterType: null;
          value: string | null;
      };

export function getColumnValue(
    tableColumn: DatabricksQueryColumn,
    appliedFilters: FilterData[],
): FormattedColumnValue {
    const columnValue = formatColumnDefault(tableColumn);
    const filter = appliedFilters.find(
        elem => tableColumn.filter && tableColumn.filter.listenTo === elem.item,
    );

    if (!filter) {
        return {
            filterType: null,
            value: (columnValue !== null && escapeString(columnValue)) || null,
        };
    }

    return {
        filterType: filter.value.type,
        ...FilterValue.match(filter.value, {
            DATE: ({ value }) => ({ isValueArray: false, value: formatDate(tableColumn, value) }),
            DATE_RANGE: ({ value: [from, to] }) => ({
                isValueArray: false,
                value: `${formatDate(tableColumn, from)} AND ${formatDate(tableColumn, to)}`,
            }),
            DIMENSION: ({ value }) => ({
                isValueArray: value.length > 1,
                value:
                    value.length === 1
                        ? escapeString(value[0].value)
                        : value.map(item => escapeString(item.value)).join(','),
            }),
            HIERARCHY: ({ value }) => ({
                isValueArray: value.values.length > 1,
                value:
                    value.values.length === 1
                        ? escapeString(value.values[0].value)
                        : value.values.map(item => escapeString(item.value)).join(','),
            }),
            NUMBER: ({ value }) => ({ isValueArray: false, value: escapeString(value) }),
            ROLE: ({ value }) => ({ isValueArray: false, value: escapeString(value.key) }),
            SCALE: ({ value }) => ({ isValueArray: false, value: escapeString(value.key) }),
            SMART_VIEW_TAG: ({ viewTag }) => ({
                isValueArray: false,
                value: escapeString(viewTag),
            }),
        }),
    };
}

function escapeString(text: DatabricksFilterDefault): string {
    return typeof text === 'string' ? `'${text}'` : text.toString();
}

function formatDate(tableColumn: DatabricksQueryColumn, date: string): string {
    const output =
        tableColumn.filter && tableColumn.filter.format
            ? DateTime.fromISO(date).toFormat(tableColumn.filter.format)
            : date;

    return escapeString(output);
}

function formatColumnDefault(tableColumn: DatabricksQueryColumn): DatabricksFilterDefault | null {
    if (!tableColumn.filter || !tableColumn.filter.default) {
        return null;
    }

    if (!dataBricksDateColumnType.includes(tableColumn.type.toLocaleLowerCase())) {
        return tableColumn.filter.default;
    }
    const dateValue = toISODateString(tableColumn.filter.default);

    if (!dateValue) {
        return tableColumn.filter.default;
    }

    return tableColumn.filter && tableColumn.filter.format
        ? DateTime.fromISO(dateValue).toFormat(tableColumn.filter.format)
        : dateValue;
}
